'use client'

import { useState } from 'react'
import { CalendarDays, ClipboardCheck } from 'lucide-react'
import { PageShell } from '@/components/ui/page-shell'
import { TeamCalendar } from '@/components/calendar/TeamCalendar'
import { CompanyCalendar } from './CompanyCalendar'
import { cn } from '@/lib/utils'

type View = 'schedule' | 'attendance'

const VIEWS = [
  ['schedule', 'Schedule', CalendarDays],
  ['attendance', 'Attendance', ClipboardCheck],
] as const

/**
 * The admin Calendar tab. Two views over the same month: the shared schedule
 * everyone sees (events, holidays, the viewer's own commitments) and the
 * team-wide attendance roll-call, which only Admin/HR get.
 */
export function CalendarWorkspace({ userId }: { userId: string }) {
  const [view, setView] = useState<View>('schedule')

  return (
    <PageShell>
      <div className="inline-flex rounded-xl bg-neutral-100 p-0.5 ring-1 ring-inset ring-neutral-200 dark:bg-white/5 dark:ring-white/10">
        {VIEWS.map(([v, label, Icon]) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            aria-pressed={view === v}
            className={cn(
              'inline-flex items-center justify-center gap-1.5 rounded-[10px] px-3 py-1.5 text-xs font-medium transition-colors',
              view === v
                ? 'bg-white text-neutral-900 shadow-sm dark:bg-white/10 dark:text-white'
                : 'text-neutral-500 hover:text-neutral-800 dark:text-white/50 dark:hover:text-white/80'
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {view === 'schedule' ? <TeamCalendar userId={userId} canManage /> : <CompanyCalendar embedded />}
    </PageShell>
  )
}
